import { Injectable } from '@angular/core'
import { Location } from '@angular/common'
import { AppPaths } from 'src/app/app-routing.module'
import { BehaviorSubject, Subject, throttleTime } from 'rxjs'
import { ActivatedRoute } from '@angular/router'
import { Layers } from 'src/app/shared/layers-config/layers'
import {
  normalizeSelectedSignTypes,
  signTypesPropertyFilterKey,
} from 'src/app/shared/utils/property-filter.utils'
import { AuthService } from './auth.service'

export interface AppState {
  layers?: string
  metrics?: string
  propertyFilters?: string
  zoom?: number
  lat?: number
  lon?: number
  timestamp?: string | null
  rolloutDate?: string | null
  timespan?: number
  selectedFeatureId?: string
  historyEnabled?: boolean
  datasource?: string
}

const initState: AppState = {
  lat: undefined,
  layers: undefined,
  metrics: undefined,
  propertyFilters: undefined,
  lon: undefined,
  zoom: undefined,
  timestamp: undefined,
  rolloutDate: undefined,
  timespan: undefined,
  selectedFeatureId: undefined,
  historyEnabled: undefined,
  datasource: undefined,
}

@Injectable({
  providedIn: 'root',
})
export class AppStateService {
  public previousState: AppState = initState
  public state: AppState = initState
  public stateChanged = new Subject<AppState>()
  public isLoadingChanged$ = new BehaviorSubject(false)
  public isLoading = false
  public currentAppPath: AppPaths | undefined

  constructor(
    public authService: AuthService,
    private location: Location,
    private route: ActivatedRoute,
  ) {
    this.route.queryParams.subscribe((params) => {
      this.state = params
      this.stateChanged.next(this.state)
    })

    this.location.onUrlChange(() => {
      const queryParams = window.location.search
      const urlParams = new URLSearchParams(queryParams)
      this.state = checkLayerPermissions(urlParams, authService)
      this.stateChanged.next(this.state)
    })

    this.isLoadingChanged$
      .pipe(throttleTime(1000, undefined, { leading: true, trailing: true }))
      .subscribe((isLoading) => {
        this.isLoading = isLoading
      })
  }

  setState(path: AppPaths, state: AppState) {
    this.previousState = this.state
    this.state = {
      ...this.state,
      ...state,
    }
    this.location.go(path, `${this.paramsToUrl()}`)

    // Keep track of the last route that wrote state so shared UI elements
    // (for example shared filter components) can update the correct app view.
    this.currentAppPath = path
  }

  /**
   * Updates in-memory state without emitting stateChanged or changing the URL.
   * Use this to prime previousState before a real setState call so that
   * hasTimestampChanged() (and similar helpers) return true even when the
   * outward value has not changed — e.g. forcing a reload for the same date.
   */
  setStateSilent(state: AppState) {
    this.previousState = this.state
    this.state = { ...this.state, ...state }
  }

  getState() {
    return this.state
  }

  getCurrentAppPath(): AppPaths | undefined {
    // Returns the last app path passed to setState (Internal/Customer/etc.).
    return this.currentAppPath
  }

  private paramsToUrl() {
    const layers = this.state?.layers ? `layers=${this.state?.layers}` : ''
    const metrics = this.state?.metrics ? `&metrics=${this.state?.metrics}` : ''
    const propertyFilters = this.state?.propertyFilters
      ? `&propertyFilters=${this.state?.propertyFilters}`
      : ''
    const zoom = this.state?.zoom ? `&zoom=${this.state?.zoom}` : ''
    const lat = this.state?.lat ? `&lat=${this.state?.lat}` : ''
    const lon = this.state?.lon ? `&lon=${this.state?.lon}` : ''
    const timestamp = this.state?.timestamp ? `&timestamp=${this.state?.timestamp}` : ''
    const rolloutDate = this.state?.rolloutDate ? `&rolloutDate=${this.state?.rolloutDate}` : ''
    const timespan = this.state?.timespan != undefined ? `&timespan=${this.state?.timespan}` : ''
    const selectedFeatureId =
      this.state?.selectedFeatureId != undefined
        ? `&selectedFeatureId=${this.state?.selectedFeatureId}`
        : ''
    const historyEnabled =
      this.state?.historyEnabled != undefined ? `&historyEnabled=${this.state?.historyEnabled}` : ''
    const datasource = this.state?.datasource ? `&datasource=${this.state?.datasource}` : ''
    return `${layers}${zoom}${lat}${lon}${metrics}${propertyFilters}${timestamp}${rolloutDate}${timespan}${selectedFeatureId}${historyEnabled}${datasource}`
  }

  public selectMetrics(filter?: string, includes: boolean = true) {
    const metrics = this.state.metrics?.split(',')
    if (metrics && filter && includes) {
      return metrics.filter((metric) => metric.startsWith(filter))
    }
    if (metrics && filter && !includes) {
      return metrics.filter((metrics) => !metrics.startsWith(filter))
    }
    return metrics ? metrics : []
  }

  public selectLayers(filter?: string, includes: boolean = true) {
    const layers = this.state.layers?.split(',')
    if (layers && filter && includes) {
      return layers.filter((layers) => layers.startsWith(filter))
    }
    if (layers && filter && !includes) {
      return layers.filter((layers) => !layers.startsWith(filter))
    }
    return layers ? layers : []
  }

  public selectSignTypes() {
    const signTypesValue = this.selectPropertyFilters()[signTypesPropertyFilterKey]
    const signTypes = signTypesValue
      ? signTypesValue.split('|').filter((signType) => signType.trim())
      : []

    return normalizeSelectedSignTypes(signTypes)
  }

  public selectPropertyFilters(): Record<string, string> {
    if (!this.state.propertyFilters) {
      return {}
    }

    return this.state.propertyFilters
      .split(',')
      .filter((entry) => entry.trim().length > 0)
      .reduce<Record<string, string>>((acc, entry) => {
        const [key, ...valueParts] = entry.split('=')
        if (!key) {
          return acc
        }

        const value = valueParts.join('=')
        acc[key] = value
        return acc
      }, {})
  }

  public selectTimestamp(): Date | undefined {
    if (this.state.timestamp) {
      return new Date(this.state.timestamp)
    }
    return undefined
  }

  public isHistoryEnabled(): boolean {
    if (this.state.historyEnabled == undefined || this.state.historyEnabled === false) {
      return false
    }
    return this.state.historyEnabled
  }

  public hasMapBoundsChanged(): boolean {
    if (this.state.lat != this.previousState.lat) {
      return true
    }
    if (this.state.lon != this.previousState.lon) {
      return true
    }
    if (this.state.zoom != this.previousState.zoom) {
      return true
    }
    return false
  }

  public hasTimestampChanged(): boolean {
    if (this.state.timestamp != this.previousState.timestamp) {
      return true
    }
    return false
  }

  public hasTimespanChanged(): boolean {
    if (this.state.timespan != this.previousState.timespan) {
      return true
    }
    return false
  }

  public getSelectedFeatureId(): string | undefined {
    if (this.state.selectedFeatureId) {
      return this.state.selectedFeatureId
    }
    return undefined
  }

  public hasSelectedFeatureIdChanged(): boolean {
    if (this.state.selectedFeatureId != this.previousState.selectedFeatureId) {
      return true
    }
    return false
  }

  public hasDatasourceParamChanged(): boolean {
    return this.state.datasource != this.previousState.datasource
  }

  public selectDatasource(): string | undefined {
    return this.state.datasource
  }

  public hasLayerVisibilityChanged(layer: string): boolean {
    const previousLayer =
      this.previousState.layers?.split(',').some((prevLayer: any) => layer == prevLayer) ?? false
    const currentLayer = this.state.layers?.split(',').some((currLayer: any) => layer == currLayer)
    if (previousLayer != currentLayer) {
      return true
    } else {
      return false
    }
  }

  public hasMetricVisibilityChanged(metric: string): boolean {
    const previousMetric = this.previousState.metrics?.includes(metric)
    const currentMetriv = this.state.layers?.includes(metric)
    if (previousMetric != currentMetriv) {
      return true
    } else {
      return false
    }
  }

  public reset(state: AppState): void {
    this.state = {
      ...this.state,
      ...state,
    }
  }
}

// Check which layers are allowed for the user
function checkLayerPermissions(urlParams: URLSearchParams, authService: AuthService): AppState {
  let params = Object.fromEntries(urlParams)
  if (params['layers'] == undefined) {
    return params
  } else {
    const permissions = authService.getUserClaims().userPermissions
    const layers = params['layers'].split(',').filter((layer: string) => layer.trim())
    const checkedLayers = layers.filter((layer: string) => {
      return Object.values(Layers).find((entry) => {
        if (layer === entry.name) {
          return entry.permissions.length === 0 ||
            entry.permissions.some((permission: any) => {
              return permissions.includes(permission)
            })
            ? layer
            : ''
        }
        return ''
      })
    })
    params['layers'] = checkedLayers.toString()
    return params
  }
}
